import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { VendorProvider } from '../VendorPovider/VendorPovider';
import { AlgoliaAnalytics } from '../AlgoliaAnalytics/AlgoliaAnalytics';
import { getAnalyticsConfig } from '../../shared/helpers/componentHelpers';

import { getPageComponent } from './getPageComponent';
import { ComponentPopup } from './ComponentPopup/ComponentPopup';
import { ComponentErrorBoundary } from './ComponentErrorBoundary';

import type { Component } from '@vgtv/api-client/lib/component';
import type { FC } from 'react';

import * as styles from './PageComponents.module.scss';

interface PageComponentsProps {
  componentsConfig: Component[];
}

// don't go anywhere below 3 with this value. current implementation of e.g. season slider requires the component to be loaded in order to set proper Structured Metadata
const PRELOADED_COMPONENT_COUNT = 3;

const Lazy: FC = ({ children }) => {
  const [isLoaded, setLoaded] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current && !isLoaded) {
      const observer = new IntersectionObserver(async (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          setLoaded(true);
        }
      });

      observer.observe(ref.current);

      return () => observer.disconnect();
    }
  }, [isLoaded]);

  return isLoaded ? <>{children}</> : <div ref={ref} />;
};

const PageComponentsBase: FC<PageComponentsProps> = ({ componentsConfig }) => {
  const components = useMemo(() => {
    return componentsConfig.map((componentConfig, index) => {
      const PageComponent = getPageComponent(componentConfig);

      let element = (
        <PageComponent
          componentConfig={componentConfig}
          isFirstComponent={index === 0}
          pageComponentIndex={index}
        />
      );

      if (componentConfig.provider) {
        element = (
          <VendorProvider vendor={componentConfig.provider}>
            {element}
          </VendorProvider>
        );
      }

      const { algolia } = getAnalyticsConfig(componentConfig);

      if (algolia) {
        element = <AlgoliaAnalytics {...algolia}>{element}</AlgoliaAnalytics>;
      }

      element = (
        <ComponentErrorBoundary key={componentConfig.id}>
          {element}
        </ComponentErrorBoundary>
      );

      return index < PRELOADED_COMPONENT_COUNT ? (
        element
      ) : (
        <Lazy key={componentConfig.id}>{element}</Lazy>
      );
    });
  }, [componentsConfig]);

  return (
    <div className={styles.container}>
      {components}
      <ComponentPopup componentsConfig={componentsConfig} />
    </div>
  );
};

export const PageComponents = memo(PageComponentsBase);
